import { useState, useRef, useEffect, useCallback } from 'react';
import { Ball, TrailCircle, CollisionEvent, BlastEvent, EscapeRing, ShapeType, SimulationConfig, AppSave, SpaceSpiralRoundConfig, UserSound, CustomMelody, BallPiece, RhythmicSimulationState } from '../types';
import { DEFAULT_CONFIG, STORAGE_KEY, GLOBAL_USER_SOUNDS_KEY, GLOBAL_CUSTOM_MELODIES_KEY } from '../constants';
import { PhysicsSystem } from '../logic/physics/PhysicsSystem';
import { BallPool } from '../services/BallPool';
import { TrailCirclePool } from '../services/TrailCirclePool';
import { safeGetItem, safeSetItem, safeSaveConfig } from '../lib/storage';

export const useSimulationState = () => {
  const [config, _setConfig] = useState<SimulationConfig>(() => {
    const saved = safeGetItem(STORAGE_KEY);
    const globalSounds = JSON.parse(safeGetItem(GLOBAL_USER_SOUNDS_KEY) || '[]');
    const globalMelodies = JSON.parse(safeGetItem(GLOBAL_CUSTOM_MELODIES_KEY) || '[]');
    
    const baseConfig = saved ? { ...DEFAULT_CONFIG, ...JSON.parse(saved) } : DEFAULT_CONFIG;
    
    // Ensure new HUD properties are initialized if missing from saved config
    if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.showHUD === undefined) baseConfig.showHUD = true;
        if (parsed.hudShowElapsedTime === undefined) baseConfig.hudShowElapsedTime = true;
        if (parsed.hudShowSpeed === undefined) baseConfig.hudShowSpeed = true;
        if (parsed.hudPosition === undefined) baseConfig.hudPosition = 'TOP_RIGHT';
        if (parsed.hudOpacity === undefined) baseConfig.hudOpacity = 0.8;
        if (parsed.hudScale === undefined) baseConfig.hudScale = 1.0;
        if (parsed.showControlIsland === undefined) baseConfig.showControlIsland = true;
        
        // Initialize new Title/Footer settings
        if (parsed.titleText === undefined) baseConfig.titleText = "";
        if (parsed.titleFontFamily === undefined) baseConfig.titleFontFamily = "Inter";
        if (parsed.titleFontWeight === undefined) baseConfig.titleFontWeight = "900";
        if (parsed.footerText === undefined) baseConfig.footerText = "";
        if (parsed.footerFontFamily === undefined) baseConfig.footerFontFamily = "Inter";
        if (parsed.footerFontWeight === undefined) baseConfig.footerFontWeight = "500";
    }
    
    // Merge global sounds/melodies
    const mergedSounds = [...(baseConfig.userSounds || [])];
    globalSounds.forEach((gs: UserSound) => {
        if (!mergedSounds.find(s => s.id === gs.id)) {
            mergedSounds.push(gs);
        }
    });

    const mergedMelodies = [...(baseConfig.customMelodies || [])];
    globalMelodies.forEach((gm: CustomMelody) => {
        if (!mergedMelodies.find(m => m.id === gm.id)) {
            mergedMelodies.push(gm);
        }
    });

    return { ...baseConfig, userSounds: mergedSounds, customMelodies: mergedMelodies };
  });

  // Custom setConfig that preserves global sounds/melodies
  const setConfig = useCallback((value: React.SetStateAction<SimulationConfig>) => {
    _setConfig((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        
        const globalSounds = JSON.parse(safeGetItem(GLOBAL_USER_SOUNDS_KEY) || '[]');
        const globalMelodies = JSON.parse(safeGetItem(GLOBAL_CUSTOM_MELODIES_KEY) || '[]');
        
        const mergedSounds = [...(next.userSounds || [])];
        globalSounds.forEach((gs: UserSound) => {
            if (!mergedSounds.find(s => s.id === gs.id)) {
                mergedSounds.push(gs);
            }
        });

        const mergedMelodies = [...(next.customMelodies || [])];
        globalMelodies.forEach((gm: CustomMelody) => {
            if (!mergedMelodies.find(m => m.id === gm.id)) {
                mergedMelodies.push(gm);
            }
        });

        return { ...next, userSounds: mergedSounds, customMelodies: mergedMelodies };
    });
  }, []);

  const [ballCount, setBallCount] = useState(0);
  const [bounceCount, setBounceCount] = useState(0);
  const bounceCountRef = useRef(0);
  useEffect(() => { bounceCountRef.current = bounceCount; }, [bounceCount]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [showUI, setShowUI] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isUploadingBallSound, setIsUploadingBallSound] = useState(false);
  const [cloudSaves, setCloudSaves] = useState<AppSave[]>([]);
  
  // Auto-save to localStorage whenever config changes
  useEffect(() => {
    safeSaveConfig(STORAGE_KEY, config);
    
    // Also update global stores if they contain new items
    const globalSounds = JSON.parse(safeGetItem(GLOBAL_USER_SOUNDS_KEY) || '[]');
    const globalMelodies = JSON.parse(safeGetItem(GLOBAL_CUSTOM_MELODIES_KEY) || '[]');
    
    let globalSoundsChanged = false;
    config.userSounds.forEach(s => {
        if (!globalSounds.find((gs: UserSound) => gs.id === s.id)) {
            globalSounds.push(s);
            globalSoundsChanged = true;
        }
    });

    let globalMelodiesChanged = false;
    config.customMelodies.forEach(m => {
        if (!globalMelodies.find((gm: CustomMelody) => gm.id === m.id)) {
            globalMelodies.push(m);
            globalMelodiesChanged = true;
        }
    });

    if (globalSoundsChanged) {
        safeSetItem(GLOBAL_USER_SOUNDS_KEY, JSON.stringify(globalSounds));
    }
    if (globalMelodiesChanged) {
        safeSetItem(GLOBAL_CUSTOM_MELODIES_KEY, JSON.stringify(globalMelodies));
    }
  }, [config]);
  
  const ballsRef = useRef<Ball[]>([]);
  const trailCirclesRef = useRef<TrailCircle[]>([]);
  const collisionEventsRef = useRef<CollisionEvent[]>([]);
  const blastEventsRef = useRef<BlastEvent[]>([]);
  const persistentSpectrumBoostRef = useRef<Float32Array>(new Float32Array(256).fill(0));
  const persistentSpectrumTargetRef = useRef<Float32Array>(new Float32Array(256).fill(0));
  const rhythmicBreakPiecesRef = useRef<BallPiece[]>([]);
  const escapeRingsRef = useRef<EscapeRing[]>([]);
  const initialEscapeRingCountRef = useRef<number>(0); 
  const requestRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(Date.now());
  const hueRef = useRef<number>(0);
  const timeScaleRef = useRef<number>(1.0);
  const isRotationStoppedRef = useRef<boolean>(false);
  const shakeRef = useRef<number>(0);
  const counterPopRef = useRef<number>(1.0);
  const timeRemainingRef = useRef<number>(10);
  
  // Space Spiral Round System
  const currentRoundRef = useRef<number>(0);
  const roundStartTimeRef = useRef<number>(0);
  const roundOverridesRef = useRef<SpaceSpiralRoundConfig | null>(null);
  
  const morphTimerRef = useRef<number>(0); 
  const morphStateRef = useRef({
      active: false,
      from: 'CIRCLE' as ShapeType,
      to: 'CIRCLE' as ShapeType,
      progress: 0
  });
  
  const escapeAccelerationBoostRef = useRef<number>(0);
  const containerRadiusRef = useRef<number>(DEFAULT_CONFIG.containerRadius);
  const speedIncreaseRateRef = useRef<number>(DEFAULT_CONFIG.speedIncreaseRate);
  const ballGrowthRateRef = useRef<number>(DEFAULT_CONFIG.ballGrowthRate);
  const containerShrinkRateRef = useRef<number>(DEFAULT_CONFIG.containerShrinkRate);
  const rhythmicIndexRef = useRef<number>(0); 
  const rhythmicTimeScaleRef = useRef<number>(DEFAULT_CONFIG.rhythmicTimeScale);
  const rhythmicTimeScaleRateRef = useRef<number>(DEFAULT_CONFIG.rhythmicTimeScaleRate);
  const rhythmicResetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rhythmicSimulationsStateRef = useRef<RhythmicSimulationState[]>([]);
  const simTimeRef = useRef<number>(0);

  const rhythmicEscapeShieldActiveRef = useRef<boolean>(false);
  const rhythmicEscapeShieldBouncesRef = useRef<number>(0);
  const rhythmicEscapeShieldTimeRef = useRef<number>(0);
  const rhythmicEscapeShieldAngleRef = useRef<number>(0);

  const physicsSystemRef = useRef<PhysicsSystem>(new PhysicsSystem()); 
  const workerRef = useRef<Worker | null>(null);
  const ballPoolRef = useRef<BallPool>(new BallPool());
  const trailCirclePoolRef = useRef<TrailCirclePool>(new TrailCirclePool());

  return {
    config, setConfig,
    ballCount, setBallCount,
    bounceCount, setBounceCount,
    bounceCountRef,
    isPlaying, setIsPlaying,
    showUI, setShowUI,
    elapsedTime, setElapsedTime,
    saveStatus, setSaveStatus,
    isAnalyzing, setIsAnalyzing,
    analysisProgress, setAnalysisProgress,
    isUploadingBallSound, setIsUploadingBallSound,
    cloudSaves, setCloudSaves,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    persistentSpectrumBoostRef,
    persistentSpectrumTargetRef,
    rhythmicBreakPiecesRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    requestRef,
    lastTimeRef,
    hueRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    containerRadiusRef,
    rhythmicIndexRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    rhythmicResetTimeoutRef,
    rhythmicSimulationsStateRef,
    simTimeRef,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef,
    physicsSystemRef,
    workerRef,
    ballPoolRef,
    trailCirclePoolRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef
  };
};
